import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import MasterModal from "./MasterModal";
import { getRanges, createRanges, updateRange } from "@/services/masters";
import AddRangeForm, {
  AddRangeFormHandle,
  RangeFormData,
} from "./ranges/AddRange";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import MasterTable from "./MasterTable";
import { Plus } from "lucide-react";

interface RangeItem {
  id: number;
  Range: string;
  Created_At?: string;
  Created_By?: string;
}

interface Range {
  id: number;
  range: string;
  createdAt?: string;
  createdBy?: string;
}

interface RangeResponse {
  results: RangeItem[];
  total_pages: number;
  count: number;
}

export interface TableColumn<T> {
  key: keyof T;
  label: string;
  render?: (row: T) => React.ReactNode;
}

export default function Ranges() {
  const { canCreate, canUpdate } = useScreenPermission("ranges");

  const [ranges, setRanges] = useState<Range[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRange, setEditingRange] = useState<Range | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const formRef = useRef<AddRangeFormHandle>(null);

  useEffect(() => {
    loadRanges(page);
  }, [page]);

  const loadRanges = async (page: number) => {
    setIsLoading(true);

    try {
      const { data, status } = await getRanges(page);

      if (status === 200 && data) {
        const isPaginated = !Array.isArray(data);

        const items: RangeItem[] = isPaginated
          ? (data as RangeResponse).results
          : data;

        const mapped: Range[] = items.map((item) => ({
          id: item.id,
          range: item.Range,
          createdAt: item.Created_At,
          createdBy: item.Created_By,
        }));

        setRanges(mapped);

        if (isPaginated) {
          const d = data as RangeResponse;
          setTotalPages(d.total_pages ?? 1);
          setTotalCount(d.count ?? 0);
        } else {
          setTotalPages(1);
          setTotalCount(items.length);
        }
      }
    } catch {
      toast.error("Error loading ranges");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (range?: Range) => {
    setEditingRange(range || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRange(null);

    setTimeout(() => {
      formRef.current?.reset();
    }, 0);
  };

  const handleSubmit = async (data: RangeFormData) => {
    formRef.current?.clearFieldErrors();
    setIsLoading(true);

    try {
      let response;

      if (editingRange) {
        response = await updateRange(editingRange.id, data);
      } else {
        response = await createRanges(data);
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(
          editingRange
            ? "Range updated successfully"
            : "Range created successfully"
        );

        handleCloseModal();
        await loadRanges(page);
      } else if (response.data && typeof response.data === "object") {
        let hasFieldErrors = false;

        if (typeof response.errors === "object") {
          Object.entries(response.errors).forEach(([key, value]) => {
            const errorText = Array.isArray(value) ? value[0] : String(value);
            if (key === "error" || key === "non_field_errors" || key === "detail") {
              toast.error(errorText);
            } else {
              formRef.current?.setFieldError(key, errorText);
              hasFieldErrors = true;
            }
          });
        }

        if (!hasFieldErrors) {
          toast.error("Failed to save range.");
        }
      }
    } catch (error) {
      console.error("Error saving range:", error);
      toast.error("Error saving range");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalSubmit = () => {
    formRef.current?.submit();
  };

  const handleAdd = () => handleOpenModal();
  const handleEdit = (item: Range) => handleOpenModal(item);

  const columns: TableColumn<Range>[] = [
    {
      key: "range",
      label: "Range",
      render: (row) => row.range,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="px-6 py-4 rounded-t-xl shadow-lg" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Ranges</h2>
            <p className="text-white text-sm mt-1">Create, edit, and manage ranges</p>
          </div>
          {canCreate && (
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Plus className="w-5 h-5" />
              Add New Range
            </button>
          )}
        </div>
      </div>
      <MasterTable
        columns={columns}
        data={ranges}
        isLoading={isLoading && ranges.length === 0}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => { setPage(p); loadRanges(p); }}
        canEdit={canUpdate}
        onEdit={canUpdate ? handleEdit : undefined}
        // canToggle={canUpdate}
        totalCount={totalCount}
      />

      <MasterModal
        isOpen={isModalOpen}
        title={editingRange ? "Edit Range" : "Add New Range"}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        isLoading={isLoading}
      >
        <AddRangeForm
          ref={formRef}
          onSubmit={handleSubmit}
          defaultValues={
            editingRange
              ? { Range: editingRange.range }
              : undefined
          }
          isLoading={isLoading}
        />
      </MasterModal>
    </div>
  );
}